import type { AppInsightsLike } from 'src/utilities/telemetry';
import { createTelemetryKey, trackTelemetryEvent } from 'src/utilities/telemetry';
export type FilterTelemetryChangeKind = 'KeywordOnly' | 'ServerFilterOnly' | 'KeywordAndServerFilter' | 'NoFilterChange';
export type FilterTelemetryKeywordState = 'None' | 'Added' | 'Changed' | 'Removed' | 'Unchanged';
export type FilterTelemetryTemporarySetAction = 'None' | 'Created' | 'Reused';

export type FilterTelemetry = {
	changeKind: FilterTelemetryChangeKind;
	keywordState: FilterTelemetryKeywordState;
	expressionChanged: boolean;
	entryStatusChanged: boolean;
	paymentStatusChanged: boolean;
	dynamicChanged: boolean;
	savedBaseHadKeyword: boolean;
	keywordLength: number;
	expressionLength: number;
	entryStatusCount: number;
	paymentStatusCount: number;
};

type FilterTelemetrySession = {
	id: string;
	baseFilter: Record<string, any>;
	applyCount: number;
	keywordOnlyApplyCount: number;
	serverFilterApplyCount: number;
	temporarySetCreateCount: number;
	temporarySetReuseCount: number;
};

type FilterTelemetryContext = {
	entryViewId: string;
	formId: string;
	isUserSpecific: string;
	isTaskView: string;
};

type FilterTelemetryTrackerOptions = {
	appInsights?: AppInsightsLike;
	getCurrentView: () => any;
	getFormId: () => string;
	serializeFilter: (filter: any) => Record<string, any>;
};

function normalizeFilterArray(values: string[] = []): string[] {
	return values.slice().sort();
}

function arraysEqual(left: string[] = [], right: string[] = []): boolean {
	left = normalizeFilterArray(left);
	right = normalizeFilterArray(right);

	return left.length === right.length && left.every((value, index) => value === right[index]);
}

function normalizeFilterText(value: string): string {
	return value || '';
}

function getBooleanProperty(value: unknown): string {
	return value ? 'true' : 'false';
}

function getViewFlag(view: any, propertyName: string, getterName: string): boolean {
	if (!view)
		return false;
	if (typeof view[getterName] === 'function')
		return !!view[getterName]();
	return !!view[propertyName];
}

export function getFilterTelemetry(baseFilter: Record<string, any> = {}, filter: Record<string, any> = {}): FilterTelemetry {
	const baseKeyword = normalizeFilterText(baseFilter.Keyword);
	const keyword = normalizeFilterText(filter.Keyword);
	const expression = normalizeFilterText(filter.Expression);
	const keywordChanged = baseKeyword !== keyword;
	const expressionChanged = normalizeFilterText(baseFilter.Expression) !== expression;
	const entryStatusChanged = !arraysEqual(baseFilter.EntryStatus, filter.EntryStatus);
	const paymentStatusChanged = !arraysEqual(baseFilter.PaymentStatus, filter.PaymentStatus);
	const dynamicChanged = !!baseFilter.IsDynamic !== !!filter.IsDynamic;
	const serverFilterChanged = expressionChanged || entryStatusChanged || paymentStatusChanged || dynamicChanged;

	let keywordState: FilterTelemetryKeywordState = 'Unchanged';
	if (!baseKeyword && !keyword)
		keywordState = 'None';
	else if (!baseKeyword && keyword)
		keywordState = 'Added';
	else if (baseKeyword && !keyword)
		keywordState = 'Removed';
	else if (keywordChanged)
		keywordState = 'Changed';

	let changeKind: FilterTelemetryChangeKind = 'NoFilterChange';
	if (keywordChanged && serverFilterChanged)
		changeKind = 'KeywordAndServerFilter';
	else if (keywordChanged)
		changeKind = 'KeywordOnly';
	else if (serverFilterChanged)
		changeKind = 'ServerFilterOnly';

	return {
		changeKind,
		keywordState,
		expressionChanged,
		entryStatusChanged,
		paymentStatusChanged,
		dynamicChanged,
		savedBaseHadKeyword: !!baseKeyword,
		keywordLength: keyword.length,
		expressionLength: expression.length,
		entryStatusCount: (filter.EntryStatus || []).length,
		paymentStatusCount: (filter.PaymentStatus || []).length
	};
}

function createFilterEditSession(baseFilter: Record<string, any>): FilterTelemetrySession {
	return {
		id: createTelemetryKey('filter'),
		baseFilter: JSON.parse(JSON.stringify(baseFilter || {})),
		applyCount: 0,
		keywordOnlyApplyCount: 0,
		serverFilterApplyCount: 0,
		temporarySetCreateCount: 0,
		temporarySetReuseCount: 0
	};
}

export class EntryViewFilterTelemetryTracker {
	private readonly options: FilterTelemetryTrackerOptions;
	private lastAppliedFilter: Record<string, any>;
	private filterTelemetrySession: FilterTelemetrySession | null;

	constructor(options: FilterTelemetryTrackerOptions) {
		this.options = options;
		this.filterTelemetrySession = null;
	}

	private getFilterTelemetryContext(view = this.options.getCurrentView()): FilterTelemetryContext {
		return {
			entryViewId: view && typeof view.get_Id === 'function' ? view.get_Id() : '',
			formId: this.options.getFormId(),
			isUserSpecific: getBooleanProperty(getViewFlag(view, 'IsUserSpecific', 'get_isUserSpecific')),
			isTaskView: getBooleanProperty(getViewFlag(view, 'IsTaskView', 'get_IsTaskView'))
		};
	}

	private getFilterTelemetryProperties(telemetry: FilterTelemetry, context: FilterTelemetryContext, temporarySetAction: FilterTelemetryTemporarySetAction, hadActiveTemporarySet: boolean): Record<string, string> {
		return {
			entryViewId: context.entryViewId,
			formId: context.formId,
			filterEditSessionId: this.filterTelemetrySession ? this.filterTelemetrySession.id : '',
			applyNumber: this.filterTelemetrySession ? this.filterTelemetrySession.applyCount.toString() : '0',
			changeKind: telemetry.changeKind,
			keywordState: telemetry.keywordState,
			expressionChanged: getBooleanProperty(telemetry.expressionChanged),
			entryStatusChanged: getBooleanProperty(telemetry.entryStatusChanged),
			paymentStatusChanged: getBooleanProperty(telemetry.paymentStatusChanged),
			dynamicChanged: getBooleanProperty(telemetry.dynamicChanged),
			savedBaseHadKeyword: getBooleanProperty(telemetry.savedBaseHadKeyword),
			activeTemporarySetBeforeApply: getBooleanProperty(hadActiveTemporarySet),
			temporarySetAction,
			isUserSpecific: context.isUserSpecific,
			isTaskView: context.isTaskView
		};
	}

	private trackFilterTelemetryEvent(eventName: string, properties: Record<string, string>, measurements: Record<string, number>) {
		trackTelemetryEvent(this.options.appInsights, eventName, properties, measurements);
	}

	private ensureFilterTelemetrySession(baseFilter: Record<string, any>) {
		if (!this.filterTelemetrySession)
			this.filterTelemetrySession = createFilterEditSession(baseFilter);
	}

	reset(filter: any) {
		this.lastAppliedFilter = this.options.serializeFilter(filter);
		this.filterTelemetrySession = null;
	}

	trackApplied(filter: any, temporarySetAction: FilterTelemetryTemporarySetAction = 'None', hadActiveTemporarySet = false) {
		const currentView = this.options.getCurrentView();
		if (!currentView)
			return;

		const currentFilter = this.options.serializeFilter(filter);
		const baseFilter = this.lastAppliedFilter || this.options.serializeFilter(currentView.get_Filter());
		const telemetry = getFilterTelemetry(baseFilter, currentFilter);
		this.ensureFilterTelemetrySession(baseFilter);

		this.filterTelemetrySession!.applyCount++;
		if (telemetry.changeKind === 'KeywordOnly')
			this.filterTelemetrySession!.keywordOnlyApplyCount++;
		else if (telemetry.changeKind === 'ServerFilterOnly' || telemetry.changeKind === 'KeywordAndServerFilter')
			this.filterTelemetrySession!.serverFilterApplyCount++;

		if (temporarySetAction === 'Created')
			this.filterTelemetrySession!.temporarySetCreateCount++;
		else if (temporarySetAction === 'Reused')
			this.filterTelemetrySession!.temporarySetReuseCount++;

		const context = this.getFilterTelemetryContext(currentView);
		const properties = this.getFilterTelemetryProperties(telemetry, context, temporarySetAction, hadActiveTemporarySet);
		const measurements = {
			keywordLength: telemetry.keywordLength,
			expressionLength: telemetry.expressionLength,
			entryStatusCount: telemetry.entryStatusCount,
			paymentStatusCount: telemetry.paymentStatusCount
		};

		this.trackFilterTelemetryEvent('EntryViewFilterApplied', properties, measurements);
		this.lastAppliedFilter = currentFilter;
	}

	trackSaved(view: any) {
		if (!this.filterTelemetrySession || this.filterTelemetrySession.applyCount === 0)
			return;

		const currentView = this.options.getCurrentView();
		const filter = this.options.serializeFilter(view && typeof view.get_Filter === 'function' ? view.get_Filter() : currentView.get_Filter());
		const telemetry = getFilterTelemetry(this.filterTelemetrySession.baseFilter, filter);
		const context = this.getFilterTelemetryContext(view || currentView);
		const properties = this.getFilterTelemetryProperties(telemetry, context, 'None', false);
		const measurements = {
			keywordLength: telemetry.keywordLength,
			expressionLength: telemetry.expressionLength,
			entryStatusCount: telemetry.entryStatusCount,
			paymentStatusCount: telemetry.paymentStatusCount,
			applyCountBeforeSave: this.filterTelemetrySession.applyCount,
			keywordOnlyApplyCountBeforeSave: this.filterTelemetrySession.keywordOnlyApplyCount,
			serverFilterApplyCountBeforeSave: this.filterTelemetrySession.serverFilterApplyCount,
			temporarySetCreateCountBeforeSave: this.filterTelemetrySession.temporarySetCreateCount,
			temporarySetReuseCountBeforeSave: this.filterTelemetrySession.temporarySetReuseCount
		};

		this.trackFilterTelemetryEvent('EntryViewFilterSaved', properties, measurements);
		this.reset(filter);
	}
}
